import {
  cloneElement,
  isValidElement,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useAnchoredPosition } from '@shared/lib/anchor-position';
import { cn } from '@shared/lib/cn';
import { usePortalContainer } from '@shared/lib/portal';
import { Divider } from '@shared/ui/divider';

import { MENU_CLASS, MENU_OFFSET_PX, type MenuItemTone, type MenuStatus } from './menu.constant';
import { MenuSkeleton } from './menu.skeleton.ui';
import { MenuItem } from './menu-item.ui';

/** 메뉴 한 줄 */
export interface MenuItemData {
  key: string;
  type?: 'item';
  /** Figma `Label` */
  label: ReactNode;
  /** Figma `Leading icon` + `Icon`(`<Icon name="…" size="sm" />`). Menu 의 `icons=true` 는 항목마다 icon 을 준 것 */
  icon?: ReactNode;
  /** Figma `Shortcut` */
  shortcut?: ReactNode;
  tone?: MenuItemTone;
  disabled?: boolean;
  onSelect?: () => void;
}

/** 항목 사이의 구분선 */
export interface MenuSeparatorData {
  key: string;
  type: 'separator';
}

export type MenuEntry = MenuItemData | MenuSeparatorData;

export interface MenuProps {
  /** 줄 목록(항목 · 구분선) */
  items: MenuEntry[];
  /** 누르면 메뉴를 여닫는 요소(버튼 하나). 없으면 메뉴만 그린다(`open` 일 때, inline 전용) */
  trigger?: ReactElement;
  /** 제어 prop */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Figma `status`. init = 항목을 불러오는 중(스켈레톤). 기본 default */
  status?: MenuStatus;
  /** portal 대신 제자리에 그린다(fe-rules 3절). 확인 페이지는 inline 으로 늘어놓는다 */
  inline?: boolean;
  /** `role="menu"` 의 이름. trigger 가 있으면 trigger 로 이름을 잇는다 */
  'aria-label'?: string;
  /** 메뉴 상자의 너비 등 */
  className?: string;
}

function isSeparator(entry: MenuEntry): entry is MenuSeparatorData {
  return entry.type === 'separator';
}

function enabledItems(menu: HTMLElement | null): HTMLElement[] {
  if (!menu) return [];
  return Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)'));
}

/**
 * 누르면 펼쳐지는 동작 목록(`role="menu"`).
 * 키보드: 위 · 아래 화살표로 항목을 옮기고(끝에서 처음으로 돈다) Home · End, Enter · Space 로 고르고,
 * Esc 로 닫고 trigger 로 포커스를 돌려준다. trigger 에서 아래 화살표를 누르면 열고 첫 항목으로 간다.
 * 고르면 닫힌다. 바깥을 누르거나 Tab 으로 벗어나도 닫힌다.
 */
export function Menu({
  items,
  trigger,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  status = 'default',
  inline = false,
  'aria-label': ariaLabel,
  className,
}: MenuProps) {
  const menuId = useId();
  const triggerId = useId();
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  const portalContainer = usePortalContainer();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  /** 열릴 때 포커스를 옮길 항목(사용자가 trigger 로 열었을 때만) */
  const focusOnOpen = useRef<'first' | 'last' | null>(null);

  const close = useCallback(
    (returnFocus: boolean) => {
      setOpen(false);
      if (returnFocus)
        anchorRef.current?.querySelector<HTMLElement>(`[id="${triggerId}"]`)?.focus();
    },
    [setOpen, triggerId],
  );

  // portal 로 띄운 메뉴를 trigger 아래(왼쪽 맞춤)에 둔다 — 화면 아래가 모자라면 위로
  useAnchoredPosition(anchorRef, floatingRef, { gap: MENU_OFFSET_PX, enabled: !inline && open });

  // 열리면 첫(또는 끝) 항목으로 포커스
  useEffect(() => {
    if (!open || !focusOnOpen.current) return;
    const list = enabledItems(menuRef.current);
    (focusOnOpen.current === 'last' ? list[list.length - 1] : list[0])?.focus();
    focusOnOpen.current = null;
  }, [open]);

  // 바깥을 누르면 닫는다(trigger 가 있을 때만 — trigger 없는 메뉴는 부르는 쪽이 연다)
  useEffect(() => {
    if (!open || trigger === undefined) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || floatingRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, trigger, setOpen]);

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const list = enabledItems(menuRef.current);
    const current = list.indexOf(document.activeElement as HTMLElement);
    let next: number;
    switch (event.key) {
      case 'ArrowDown':
        next = current < 0 ? 0 : (current + 1) % list.length;
        break;
      case 'ArrowUp':
        next = current < 0 ? list.length - 1 : (current - 1 + list.length) % list.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = list.length - 1;
        break;
      case 'Enter':
      case ' ':
        if (current >= 0) {
          event.preventDefault();
          list[current]?.click();
        }
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      case 'Tab':
        if (trigger !== undefined) setOpen(false);
        return;
      default:
        return;
    }
    event.preventDefault();
    if (list.length > 0) list[next]?.focus();
  }

  function select(item: MenuItemData) {
    item.onSelect?.();
    if (trigger !== undefined) close(true);
  }

  const labelProps = trigger !== undefined ? { 'aria-labelledby': triggerId } : {};

  const menu =
    status === 'init' ? (
      <MenuSkeleton
        ref={menuRef}
        id={menuId}
        role="menu"
        aria-label={ariaLabel}
        {...(ariaLabel ? {} : labelProps)}
        className={className}
      />
    ) : (
      <div
        ref={menuRef}
        id={menuId}
        role="menu"
        aria-orientation="vertical"
        aria-label={ariaLabel}
        {...(ariaLabel ? {} : labelProps)}
        data-status={status}
        onKeyDown={handleMenuKeyDown}
        className={cn(MENU_CLASS, className)}
      >
        {items.map((entry) =>
          isSeparator(entry) ? (
            <div key={entry.key} role="separator" className="py-1">
              <Divider tone="default" decorative />
            </div>
          ) : (
            <MenuItem
              key={entry.key}
              icon={entry.icon}
              shortcut={entry.shortcut}
              tone={entry.tone}
              disabled={entry.disabled}
              onSelect={() => select(entry)}
            >
              {entry.label}
            </MenuItem>
          ),
        )}
      </div>
    );

  if (trigger === undefined) {
    if (!open) return null;
    return inline ? menu : createPortal(menu, portalContainer);
  }

  function isFromTrigger(target: EventTarget): boolean {
    return target instanceof Element && target.closest(`[id="${triggerId}"]`) !== null;
  }

  function handleTriggerClick(event: ReactMouseEvent<HTMLSpanElement>) {
    if (event.defaultPrevented || !isFromTrigger(event.target)) return;
    if (!open) focusOnOpen.current = 'first';
    setOpen(!open);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    if (event.defaultPrevented || !isFromTrigger(event.target)) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const edge = event.key === 'ArrowDown' ? 'first' : 'last';
      if (open) {
        const list = enabledItems(menuRef.current);
        (edge === 'first' ? list[0] : list[list.length - 1])?.focus();
      } else {
        focusOnOpen.current = edge;
        setOpen(true);
      }
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  const triggerNode = isValidElement(trigger)
    ? cloneElement(
        trigger as ReactElement<{
          id?: string;
          'aria-haspopup'?: 'menu';
          'aria-expanded'?: boolean;
          'aria-controls'?: string;
        }>,
        {
          id: triggerId,
          'aria-haspopup': 'menu',
          'aria-expanded': open,
          'aria-controls': open ? menuId : undefined,
        },
      )
    : trigger;

  return (
    // 누르기 · 키는 trigger 에서 올라온 것만 받는다(trigger 의 onClick 은 그대로 둔다)
    <span
      ref={anchorRef}
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      className={cn('inline-flex', inline && 'relative')}
    >
      {triggerNode}
      {open && inline ? (
        <div ref={floatingRef} className="absolute top-full left-0 z-(--z-dropdown) mt-1">
          {menu}
        </div>
      ) : null}
      {open && !inline
        ? createPortal(
            <div ref={floatingRef} className="invisible fixed top-0 left-0 z-(--z-dropdown)">
              {menu}
            </div>,
            portalContainer,
          )
        : null}
    </span>
  );
}
